import type { MetaFunction } from "@remix-run/cloudflare";
import { useState } from "react";
import { Link } from "@remix-run/react";
import { Mail, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { SplashScreen } from "~/components/SplashScreen";
import styles from "~/styles/auth.module.css";

export const meta: MetaFunction = () => [
  { title: "Reset Password — Prodigium Account" },
  { name: "description", content: "Reset your Prodigium Account password." },
];

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setLoading(true);
    // TODO: Wire to actual password-reset API
    setTimeout(() => {
      setLoading(false);
      setSent(true);
    }, 1200);
  };

  return (
    <>
      <SplashScreen name="RESET" tag="Prodigium Account" />
      <div className={styles.card}>
        <div className={styles.header}>
          <p className={styles.tag}>Prodigium Account</p>
          <h1 className={styles.title}>Reset password</h1>
          <p className={styles.subtitle}>
            {sent
              ? "Check your inbox for a reset link."
              : "Enter your email and we'll send you a link to reset your password."}
          </p>
        </div>

        {error && (
          <div className={styles.error} style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        {sent ? (
          <div style={{ textAlign: "center", animation: "cardIn 0.4s ease both" }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "rgba(52, 199, 89, 0.10)",
                border: "1px solid rgba(52, 199, 89, 0.25)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 20,
              }}
            >
              <CheckCircle2 size={24} strokeWidth={1.5} color="#34C759" />
            </div>
            <p
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.55)",
                lineHeight: 1.7,
                maxWidth: 320,
                margin: "0 auto 24px",
              }}
            >
              If an account exists for <strong style={{ color: "#fff" }}>{email}</strong>,
              you will receive an email with instructions to reset your password.
            </p>
            <Link
              to="/account/login"
              className={styles.submitBtn}
              style={{ textDecoration: "none", maxWidth: 280, margin: "0 auto" }}
            >
              <ArrowLeft size={14} strokeWidth={2} /> Back to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="email">
                Email
              </label>
              <div className={styles.inputWrap}>
                <span className={styles.icon}>
                  <Mail size={14} strokeWidth={1.5} />
                </span>
                <input
                  id="email"
                  type="email"
                  className={styles.input}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className={styles.actions}>
              <button type="submit" className={styles.submitBtn} disabled={loading}>
                {loading ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        border: "2px solid rgba(0,0,0,0.2)",
                        borderTopColor: "#000",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                        display: "inline-block",
                      }}
                    />
                    Sending…
                  </span>
                ) : (
                  <>
                    Send Reset Link <ArrowRight size={14} strokeWidth={2} />
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {!sent && (
          <div className={styles.footerLinks}>
            <p className={styles.footerText}>
              Remember your password?{" "}
              <Link to="/account/login" className={styles.link}>
                Sign in
              </Link>
            </p>
          </div>
        )}
      </div>

      <Link to="/" className={styles.backLink} style={{ marginTop: 24 }}>
        ← Back to OS
      </Link>
    </>
  );
}
